import { createClient, type SupabaseClient } from "@supabase/supabase-js";

import { createQueuedFetch, createRequestQueue } from "./requestQueue.mjs";

/**
 * Supabase client singleton. RLS policies enforce tenant isolation by
 * `tenant_id` column; the anon key is public-safe because rows are
 * filtered server-side per the authenticated session.
 *
 * Missing env vars do NOT throw at the call site. The pre-Phase-6
 * local-dev workflow needs `/widget-gallery` to render with mock
 * data even when no Supabase config is present. Callers that need a
 * live client should branch on `isSupabaseConfigured()` and surface
 * a useful message; the legacy `getSupabase()` still throws but only
 * if called when not configured.
 */

let cached: SupabaseClient | null = null;

/**
 * AJ 2026-10-01 (speed, and the errors a fast click-through caused): every
 * database read goes through one queue (requestQueue.mjs): a ceiling on reads
 * in flight, the page on screen first, and one more try for a read the
 * database cancelled for time or that failed in transit. Writes, sign-in and
 * everything that is not a read pass straight through.
 */
const readQueue = createRequestQueue({
  currentOwner: () => (typeof window !== "undefined" ? window.location.pathname : ""),
});
const queuedFetch = createQueuedFetch({
  baseFetch: (input, init) => fetch(input, init),
  queue: readQueue,
  onRetry: ({ kind, attempt, shape }) =>
    console.warn(`read retried (${kind}, attempt ${attempt}): ${shape.slice(0, 120)}`),
});

function readEnv(name: string): string | null {
  const v = import.meta.env[name as keyof ImportMetaEnv];
  if (typeof v !== "string" || v.length === 0) return null;
  return v;
}

export function isSupabaseConfigured(): boolean {
  return readEnv("VITE_SUPABASE_URL") !== null && readEnv("VITE_SUPABASE_ANON_KEY") !== null;
}

export function getSupabase(): SupabaseClient {
  if (cached) return cached;
  const url = readEnv("VITE_SUPABASE_URL");
  const key = readEnv("VITE_SUPABASE_ANON_KEY");
  if (!url || !key) {
    throw new Error(
      "Supabase is not configured. Set VITE_SUPABASE_URL + VITE_SUPABASE_ANON_KEY " +
        "in dashboard/.env.local (see dashboard/.env.example).",
    );
  }
  cached = createClient(url, key, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
    global: { fetch: queuedFetch },
  });
  return cached;
}

/** Reset the singleton. Test-only. */
export function _resetSupabaseForTests(): void {
  cached = null;
}
